import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import CreateCustomerModal from "./CreateCustomerModal";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ClientRowObj } from "..";
import Searchbox from "components/fields/Searchbox";
import Pagination from "components/pagination";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import moment from "moment";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import { uploadKycDocument } from "api/graphQL_customers";
import visa from "assets/svg/card_type/visa.svg";
import MasterCard from "assets/svg/card_type/mastercard.svg";
import Discover from "assets/svg/card_type/discover.svg";
import Amex from "assets/svg/card_type/amex.svg";
import MaestroCard from "assets/svg/card_type/maestro.svg";
import DinersClub from "assets/svg/card_type/dinersclub.svg";
import JCB from "assets/svg/card_type/jcb.svg";
import UnionPay from "assets/svg/card_type/unionpay.svg";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import toast from "react-hot-toast";
import FilterModal from "./FilterModal";
import { getAllClients } from "api/clients";
import ExpandedCustomer from "./ExpandedCustomer";
import { ClientContext } from "ClientProvider";

const columnHelper = createColumnHelper<ClientRowObj>();

function CustomerTable(props: {
  tableData: any;
  fetchClients: () => void;
  isLoading: boolean;
  roleData?: any;
  page: number;
  setPage: any;
  totalpage: number;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  setPageSize: any;
  allGateways: any;
  onValueChange: (value: string) => void;
  allOrgDetails: any;
  filter: any; 
  setFilter: (value: any) => void;
  setClearClick: () => void;
  filterData: () => void;
}) {
  const {
    tableData, fetchClients, roleData, page, setPage, currentPage,
    totalpage, totalItems, pageSize, setPageSize, allGateways, onValueChange, isLoading,allOrgDetails,setFilter,filter,setClearClick,filterData
  } = props;

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState<any[]>([]);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<any[]>([]);
  const [activeGatewayTransactions, setActiveGatewayTransactions] = useState("");
  const [activePaymentMethod, setActivePaymentMethod] = useState("");
  const [uploadingKYC, setUploadingKYC] = useState("");
  const fetchedCustomerIds = useRef<Set<string>>(new Set());

  const [subClientsData, setSubClientsData] = useState([]);

  const { singleRoleData, setSingleRoleData } = useContext(ClientContext);

  const resetRow = () => {
    setExpandedRow(null);
    setActiveGatewayTransactions("");
    setActivePaymentMethod("");
  }

  React.useEffect(() => { setExpandedRow(null); setActiveGatewayTransactions(""); setActivePaymentMethod(""); }, [page, pageSize]);

  useEffect(() => {
    getAllGatewaysSecond("")
      .then((d) => setPaymentData(d ?? []))
      .catch(() => {});

    setData(tableData);
  }, [tableData]);

  const getSubClientsData = () => {
      getAllClients(0)
      .then((data) => setSubClientsData(data[0]))
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong while fetching clients"))
      .finally(() => {  });
  };
  
  React.useEffect(() => { getSubClientsData(); }, []);

  const handleValueChange = (e: any) => { setExpandedRow(null); setActiveGatewayTransactions(""); setActivePaymentMethod(""); onValueChange(e); };
  const handleExpand = (rowId: string) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId));
  };
  const getGatewayName = (id: any) => paymentData.find((g) => g.id === id)?.name ?? "Unknown";
  const openGatewayTransactions = (val: any) => setActiveGatewayTransactions(activeGatewayTransactions === val ? "" : val);
  const openPaymentMethods = (val: any) => setActivePaymentMethod(activePaymentMethod === val ? "" : val);

  const getCardSVG = (type: string) => {
    const map: any = { VISA: visa, MasterCard, Discover, Amex, MaestroCard, DinersClub, JCB, UnionPay };
    return map[type] ? <img style={{ maxWidth: "65px" }} src={map[type]} alt={type} title={type} /> : "-";
  };

  const cardPatterns: Record<string, RegExp> = {
    VISA: /^4[0-9]{12}(?:[0-9]{3})?$/,
    MasterCard: /^5[1-5][0-9]{14}$|^2[2-7][0-9]{14}$/,
    Discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
    Amex: /^3[47][0-9]{13}$/,
    MaestroCard: /^(5018|5020|5038|5893|6304|6759|6761|6762|6763)[0-9]{8,15}$/,
    DinersClub: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    JCB: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/,
    UnionPay: /^62\d{12,19}$/,
  };

  const getCardType = (num: string) => {
    const cleaned = num.replace(/\D/g, "");
    for (const [type, pattern] of Object.entries(cardPatterns)) {
      if (pattern.test(cleaned)) return type;
    }
    return "Unknown";
  };

  const handleFileChange = async (event: any, kycId: any, customerId: any) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpg", "image/jpeg", "image/svg", "image/svg+xml"];
    if (!allowed.includes(file.type)) { toast.error("Only PNG and SVG files are allowed."); return; }
    setUploadingKYC(kycId);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      await uploadKycDocument({ customer_id: customerId, kyc_id: kycId, document: reader.result as string })
        .then((d: any) => { setUploadingKYC(""); toast.success(d.message ?? "Document Uploaded"); fetchClients(); })
        .catch(() => setUploadingKYC(""));
    };
  };

  const columns = [
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="clt-th">Created Date</span>,
      cell: (info: any) => (
        <div
          className="clt-expand-cell"
          onClick={() => handleExpand(info?.row?.original?.customer_id)}
        >
          {expandedRow === info?.row?.original?.customer_id
            ? <AiFillMinusCircle className="clt-expand-icon clt-expand-icon--open" />
            : <AiFillPlusCircle className="clt-expand-icon" />}
          <span>{moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss")}</span>
        </div>
      ),
    }),
    columnHelper.accessor("customer_id", {
      id: "customer_id",
      header: () => <span className="clt-th">Customer ID</span>,
      cell: (info: any) => <div className="clt-mono"><ShortTruncateCopy info={info?.getValue()} showCopy={true} /></div>,
    }),
    columnHelper.accessor("first_name", {
      id: "first_name",
      header: () => <span className="clt-th">Name</span>,
      cell: (info: any) => (
        <div className="clt-name-cell">
          <div className="clt-avatar">
            {info?.row?.original?.first_name?.charAt(0)?.toUpperCase() || "C"}
          </div>
          <span className="clt-name">{info?.row?.original?.first_name} {info?.row?.original?.last_name}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.first_name || "";
        const b = rowB.original.first_name || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.customer_id).localeCompare(String(rowB.original.customer_id));
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <span className="clt-th">Email</span>,
      cell: (info) => <a href={`mailto:${info.getValue()}`} className="clt-link">{info.getValue()}</a>,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.email || "";
        const b = rowB.original.email || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.customer_id).localeCompare(String(rowB.original.customer_id));
      },
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => <span className="clt-th">Phone</span>,
      cell: (info) => <a href={`tel:${info.getValue()}`} className="clt-phone">{info.getValue()}</a>,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.phone || "";
        const b = rowB.original.phone || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.customer_id).localeCompare(String(rowB.original.customer_id));
      },
    }),
    columnHelper.accessor("source_client", {
      id: "source_client",
      header: () => <span className="clt-th">Source Client</span>,
      cell: (info: any) => <span className="clt-source">{info?.getValue()}</span>,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.source_client || "";
        const b = rowB.original.source_client || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.customer_id).localeCompare(String(rowB.original.customer_id));
      },
    }),
    columnHelper.accessor("total_transactions", {
      id: "total_transactions",
      header: () => <span className="clt-th">Transactions</span>,
      cell: (info: any) => (
        <div className="clt-txn-pill">
          <div className="clt-txn-dot" />
          <span>{info?.getValue() ?? 0}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.total_transactions || "";
        const b = rowB.original.total_transactions || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.customer_id).localeCompare(String(rowB.original.customer_id));
      },
    }),
  ];
  
  const table = useReactTable({
    data,
    columns,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  

  return (
    <div className="clt-card">
      {/* Header */}
      <div className="clt-header">
        <div className="clt-header-left">
          <div className="clt-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h3 className="clt-title">All Customers</h3>
            <p className="clt-subtitle">{totalItems > 0 ? `${totalItems} total customer${totalItems !== 1 ? "s" : ""}` : "No customers found"}</p>
          </div>
        </div>
        <div className="clt-search-wrap">
          <FilterModal
            subClientsData={subClientsData} setPage={props.setPage}
            filter={filter} setFilter={setFilter} roleData={roleData}
            setClearClick={setClearClick} 
            fetchAllsettlement={fetchClients}
            allOrgDetails={allOrgDetails}
            filterData={filterData}
            resetRow= {resetRow}
            setPageSize={setPageSize}
          />
          {singleRoleData?.[0]?.customers?.value?.add_customer && (
            <CreateCustomerModal
              fetchClients={fetchClients}
              roleData={singleRoleData}
            />
          )}
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div> 

      {/* Table */}
      <div className="clt-table-wrap">
        {isLoading ? (
          <div className="clt-loading">
            <div className="clt-spinner" />
            <p className="clt-loading-txt">Loading customers…</p>
          </div>
        ) : (
          <table className="clt-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} colSpan={h.colSpan} onClick={h.column.getToggleSortingHandler()} className="clt-thead-th">
                      <div className="clt-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="clt-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> : <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <React.Fragment key={row.id}>
                    <tr className={`clt-tbody-tr${ri % 2 !== 0 ? " clt-tbody-tr--alt" : ""}${expandedRow === row.original.customer_id ? " clt-tbody-tr--expanded" : ""}`}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="clt-tbody-td">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {expandedRow === row.original.customer_id && (
                      <ExpandedCustomer
                          row={row}
                          roleData={roleData}
                          paymentData={paymentData}
                          activeGatewayTransactions={activeGatewayTransactions}
                          activePaymentMethod={activePaymentMethod}
                          openGatewayTransactions={openGatewayTransactions}
                          openPaymentMethods={openPaymentMethods}
                          getGatewayName={getGatewayName}
                          getCardSVG={getCardSVG}
                          getCardType={getCardType}
                          uploadingKYC={uploadingKYC}
                          handleFileChange={handleFileChange}
                          fetchClients={fetchClients}
                          fetchedCustomerIds={fetchedCustomerIds}
                          setActiveGatewayTransactions={setActiveGatewayTransactions}
                          setActivePaymentMethod={setActivePaymentMethod}
                      />
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className="clt-empty">
                      <div className="clt-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                        </svg>
                      </div>
                      <p className="clt-empty-title">No customers found</p>
                      <p className="clt-empty-sub">Try adjusting your search or add a new customer</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="clt-pagination-wrap">
        <Pagination setPage={setPage} page={page} totalpage={totalpage} currentPage={currentPage} pageSize={pageSize} setPageSize={setPageSize} arraySize={[50, 100, 200]} />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .clt-card { font-family: 'Plus Jakarta Sans', sans-serif; background: #fff; border: 1px solid #e2e8f0; border-radius: 18px; overflow: hidden; box-shadow: 0 2px 8px rgba(15,23,42,.06); }

        .clt-header { padding: 20px 24px 16px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
        .clt-header-left { display: flex; align-items: center; gap: 12px; }
        .clt-header-icon { width: 40px; height: 40px; border-radius: 11px; background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; display: flex; align-items: center; justify-content: center; color: #16a34a; flex-shrink: 0; }
        .clt-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -.2px; margin-bottom: 2px; line-height: 22px; }
        .clt-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .clt-search-wrap { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

        .clt-table-wrap { overflow-x: auto; }
        .clt-table-wrap::-webkit-scrollbar { height: 4px; }
        .clt-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .clt-table { width: 100%; border-collapse: collapse; }

        .clt-thead-th { padding: 12px 16px 10px; background: #f8fafc; border-bottom: 1.5px solid #e2e8f0; text-align: left; cursor: pointer; white-space: nowrap; user-select: none; }
        .clt-thead-th:hover { background: #f1f5f9; }
        .clt-th-inner { display: flex; align-items: center; gap: 5px; }
        .clt-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: .5px; }
        .clt-sort-icon { color: #94a3b8; display: flex; align-items: center; }
        .clt-thead-th:hover .clt-sort-icon { color: #16a34a; }

        .clt-tbody-tr { transition: background .12s; }
        .clt-tbody-tr:hover { background: #f8fafc; }
        .clt-tbody-tr--alt { background: #fafbfc; }
        .clt-tbody-tr--alt:hover { background: #f1f5f9; }
        .clt-tbody-tr--expanded { background: #f0fdf4 !important; }
        .clt-tbody-td { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Cells */
        .clt-expand-cell { display: flex; align-items: center; gap: 7px; cursor: pointer; font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; }
        .clt-expand-cell--sm { gap: 6px; font-size: 12.5px; }
        .clt-expand-icon { width: 18px; height: 18px; color: #94a3b8; flex-shrink: 0; transition: color .15s; }
        .clt-expand-icon--open { color: #16a34a; }
        .clt-expand-cell:hover .clt-expand-icon { color: #16a34a; }

        .clt-name-cell { display: flex; align-items: center; gap: 9px; }
        .clt-avatar { width: 30px; height: 30px; border-radius: 8px; background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #15803d; flex-shrink: 0; }
        .clt-name { font-size: 13px; font-weight: 600; color: #0f172a; }
        .clt-link { font-size: 13px; font-weight: 500; color: #2563eb; text-decoration: none; }
        .clt-link:hover { text-decoration: underline; }
        .clt-phone { font-size: 13px; font-weight: 500; color: #0f172a; text-decoration: none; }
        .clt-source { font-size: 13px; font-weight: 500; color: #0f172a; }
        .clt-mono { font-size: 12.5px; font-weight: 700; }

        .clt-txn-pill { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 7px; background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #1d4ed8; font-size: 12.5px; font-weight: 700; }
        .clt-txn-pill--sm { padding: 3px 8px; font-size: 12px; }
        .clt-txn-dot { width: 6px; height: 6px; border-radius: 50%; background: #2563eb; }

        /* Expanded row */
        .clt-expanded-row { background: #f8fafc; }
        .clt-expanded-td { padding: 0; border-bottom: 2px solid #e2e8f0; }
        .clt-expanded-inner { padding: 20px 24px; display: flex; flex-direction: column; gap: 20px; }

        /* Sections inside expanded */
        .clt-section { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
        .clt-section-header { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #f8fafc; border-bottom: 1px solid #f1f5f9; }
        .clt-section-icon { width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .clt-section-icon--blue   { background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #2563eb; }
        .clt-section-icon--amber  { background: linear-gradient(135deg,#fffbeb,#fef3c7); border: 1.5px solid #fde68a; color: #d97706; }
        .clt-section-icon--green  { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; color: #16a34a; }
        .clt-section-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; color: #7c3aed; }
        .clt-section-title { font-size: 13px; font-weight: 700; color: #0f172a; }
        .clt-empty-section { padding: 20px; font-size: 13px; color: #94a3b8; font-weight: 500; text-align: center; font-style: italic; }

        /* Inner tables */
        .clt-inner-table-wrap { overflow-x: auto; }
        .clt-inner-table { width: 100%; border-collapse: collapse; }
        .clt-inner-th { text-align: left;padding: 10px 14px 8px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; font-size: 11px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: .5px; white-space: nowrap; }
        .clt-inner-tr { transition: background .12s; }
        .clt-inner-tr:hover { background: #f8fafc; }
        .clt-inner-td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; font-size: 13px; font-weight: 600; color: #374151; vertical-align: middle; }
        .clt-inner-td-nested { padding: 12px; background: #f1f5f9; }
        .clt-nowrap { white-space: nowrap; }
        .clt-upper { text-transform: uppercase; }
        .clt-gw-name { display: flex; align-items: center; gap: 8px; }

        /* KYC status */
        .clt-kyc-status { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 6px; font-size: 11.5px; font-weight: 700; text-transform: uppercase; }
        .clt-kyc-status--verified { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .clt-kyc-status--unverified { background: #fff1f2; color: #be123c; border: 1px solid #fecdd3; }

        /* KYC actions */
        .clt-kyc-actions { display: flex; align-items: center; gap: 6px; }
        .clt-kyc-action-btn { width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center; cursor: pointer; border: 1.5px solid; transition: all .15s; background: none; }
        .clt-kyc-action-btn--upload { border-color: #bfdbfe; color: #2563eb; background: #eff6ff; }
        .clt-kyc-action-btn--upload:hover { background: #dbeafe; }
        .clt-kyc-action-btn--done { border-color: #bbf7d0; color: #16a34a; background: #f0fdf4; cursor: default; }
        .clt-kyc-action-btn--view { border-color: #bfdbfe; color: #2563eb; background: #eff6ff; }
        .clt-kyc-action-btn--view:hover { background: #dbeafe; }
        .clt-kyc-spinner { width: 20px; height: 20px; border: 2px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: cltSpin .7s linear infinite; }

        /* Payment method type badge */
        .clt-pm-type-badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 5px; font-size: 11.5px; font-weight: 700; text-transform: capitalize; background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1px solid #bfdbfe; color: #1d4ed8; }

        /* Empty */
        .clt-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px 24px; gap: 10px; }
        .clt-empty-icon { width: 52px; height: 52px; border-radius: 13px; background: #f8fafc; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .clt-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .clt-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; text-align: center; }

        /* Loading */
        .clt-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px; gap: 12px; }
        .clt-spinner { width: 28px; height: 28px; border: 3px solid #e2e8f0; border-top-color: #16a34a; border-radius: 50%; animation: cltSpin .7s linear infinite; }
        @keyframes cltSpin { to{transform:rotate(360deg)} }
        .clt-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* Pagination */
        .clt-pagination-wrap { padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #fafbfc; }

        @media (max-width: 700px) {
          .clt-header { flex-direction: column; align-items: flex-start; }
          .clt-search-wrap { width: 100%; }
          .clt-thead-th, .clt-tbody-td { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
}

export default CustomerTable;